/**
 * Word budget for one playbook field or one reference-PDF extract.
 * Count words by splitting on whitespace. Role instructions and each
 * reference PDF are separate budgets.
 */

export const INSTRUCTION_WORD_LIMIT = 1500;

export const INSTRUCTION_SUMMARIZE_KINDS = [
  "review-role",
  "review-guidelines",
  "review-additional",
  "draft-role",
  "draft-additional",
] as const;

export type InstructionSummarizeKind = (typeof INSTRUCTION_SUMMARIZE_KINDS)[number];

export interface InstructionDocumentPart {
  fileName?: string | null;
  extractedText?: string | null;
}

export class InstructionLimitError extends Error {
  constructor(message = instructionLimitMessage()) {
    super(message);
    this.name = "InstructionLimitError";
  }
}

/**
 * Words are non-empty whitespace-separated tokens.
 */
export function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function instructionLimitMessage(): string {
  const limit = INSTRUCTION_WORD_LIMIT.toLocaleString("en-US");
  return `Instructions must be ${limit} words or fewer.`;
}

export function formatInstructionWordCount(count: number): string {
  const formattedCount = count.toLocaleString("en-US");
  const formattedLimit = INSTRUCTION_WORD_LIMIT.toLocaleString("en-US");
  const base = `${formattedCount} / ${formattedLimit} words`;
  if (count > INSTRUCTION_WORD_LIMIT) {
    return `${base}. ${instructionLimitMessage()}`;
  }
  return base;
}

export function isInstructionWithinLimit(text: string): boolean {
  return countWords(text) <= INSTRUCTION_WORD_LIMIT;
}

export function assertInstructionWithinLimit(text: string): void {
  if (!isInstructionWithinLimit(text)) {
    throw new InstructionLimitError();
  }
}

/**
 * Role or guideline text and each reference extract have their own budget.
 * A short instruction plus a short PDF summary can be saved together.
 */
export function assertInstructionPartsWithinLimit(
  baseText: string,
  documents: InstructionDocumentPart[]
): void {
  assertInstructionWithinLimit(baseText);
  for (const document of documents) {
    const extracted = typeof document.extractedText === "string" ? document.extractedText : "";
    if (extracted.trim()) assertInstructionWithinLimit(extracted);
  }
}

export function isInstructionSummarizeKind(value: unknown): value is InstructionSummarizeKind {
  return (
    typeof value === "string" &&
    (INSTRUCTION_SUMMARIZE_KINDS as readonly string[]).includes(value)
  );
}

/**
 * Joins typed instructions with reference-PDF extracts the same way review
 * and draft runs assemble the prompt.
 */
export function assembleInstructionWithDocuments(
  baseText: string,
  documents: InstructionDocumentPart[]
): string {
  const base = baseText.trim();
  const docParts: string[] = [];

  for (const doc of documents) {
    const extracted = typeof doc.extractedText === "string" ? doc.extractedText.trim() : "";
    if (!extracted) continue;
    const fileName =
      typeof doc.fileName === "string" && doc.fileName.trim() ? doc.fileName.trim() : "document.pdf";
    docParts.push(`[Reference document: ${fileName}]\n${extracted}`);
  }

  const segments = [base, ...docParts].filter(Boolean);
  return segments.length ? segments.join("\n\n---\n\n") : "";
}
